import React from 'react';
import { Box, FormHelperText, styled } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import { MultiDateCalendar } from 'redesign';
import { IconCaretLeft } from 'assets/img';
import { useMultiDayDatesCalendar } from './use-multi-day-dates-calendar';
import { MultiDayDatesCalendarProps } from './multi-day-dates-calendar.types';
import { WorkFormContext } from '../../work-form.types';
import { MULTI_DAY_WORK_FORM_FIELDS } from '../multi-day-work.types';

export const MultiDayDatesCalendar = ({ allowsPast }: MultiDayDatesCalendarProps) => {
  const {
    formState: { errors },
  } = useFormContext<WorkFormContext>();
  const { dayLabels, minDate, monthLabels, onChange, selectedDates, today } =
    useMultiDayDatesCalendar({ allowsPast });
  const datesValidationMessage = errors[MULTI_DAY_WORK_FORM_FIELDS.multiDayDates]?.message;

  return (
    <StyledCalendarGrid>
      {datesValidationMessage ? (
        <CalendarValidationError error data-testid="multi-day-dates-calendar-error">
          {datesValidationMessage}
        </CalendarValidationError>
      ) : null}
      <CalendarWrapper data-testid="multi-day-dates-calendar">
        <MultiDateCalendar
          value={selectedDates}
          currentDate={today}
          onChange={onChange}
          weekStartDayIndex={1}
          months={monthLabels}
          weekDays={dayLabels}
          {...(minDate ? { minDate } : {})}
          buttons
          multiple
          disableMonthPicker
          disableYearPicker
          numberOfMonths={2}
          renderButton={CalendarNavigationButton}
        />
      </CalendarWrapper>
    </StyledCalendarGrid>
  );
};

const CalendarNavigationButton = (
  direction: string,
  handleClick: React.MouseEventHandler<HTMLButtonElement> | undefined,
  disabled: boolean
) => {
  if (disabled) {
    return <StyledArrowButton disabled />;
  }

  return (
    <StyledArrowButton onClick={handleClick}>
      <StyledArrow style={direction === 'right' ? { transform: 'rotate(180deg)' } : {}} />
    </StyledArrowButton>
  );
};

const StyledCalendarGrid = styled('section')(({ theme }) => ({
  padding: theme.spacing(0, 3),
}));

const CalendarValidationError = styled(FormHelperText)(({ theme }) => ({
  position: 'relative',
  bottom: 'unset',
  marginBottom: theme.spacing(2),
  whiteSpace: 'normal',
  textOverflow: 'clip',
}));

const dateSize = '36px';

const CalendarWrapper = styled(Box)(() => ({
  '&& .rmdp-week-day': {
    width: dateSize,
    height: dateSize,
  },

  '&& .rmdp-day': {
    width: dateSize,
    height: dateSize,
    margin: '2px',
  },

  '&& .rmdp-header': {
    height: 'auto',
    '& .rmdp-header-values': {
      '& > span': {
        paddingLeft: '1ch',
      },
    },
  },
}));

const StyledArrow = styled(IconCaretLeft)(() => ({
  height: '27px',
  width: '27px',
}));

const StyledArrowButton = styled('button')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',

  height: '39px',
  width: '39px',
  background: 'inherit',
  border: 'none',
  borderRadius: 8,
  padding: 0,

  '&:not(:disabled):hover': {
    background: theme.palette.lwPrimaryAccentLight[10],
    cursor: 'pointer',
    '& svg': {
      '& path': { stroke: theme.palette.lwPrimaryAccentDark[100] },
    },
  },
}));
